import { canAuthorFieldDocument } from '@mio/shared';
import { Download, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';
import { LoadingStatus } from '@/shared/ui/spinner';

import {
  describeFieldSource,
  documentFormatLabels,
  documentKindLabels,
} from '../field-object-labels';

import { FieldDeliveryForm } from './field-delivery-form';
import { FieldEmpty, FieldPanelHeading, FieldStatus, FieldTime } from './field-primitives';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import './field-objects.css';

import type { FieldPanelProps } from './field-types';
import type { FieldActorId, FieldCommand, FieldDocument } from '@mio/shared';

type DocumentKind = Extract<FieldCommand['action'], { type: 'document.request' }>['kind'];
const kinds: DocumentKind[] = [
  'requirements',
  'policy',
  'architecture',
  'runbook',
  'decision',
  'report',
  'handoff',
  'postmortem',
  'test_plan',
  'scope',
];

interface Props extends FieldPanelProps {
  onDownload: (document: FieldDocument, format: FieldDocument['formats'][number]) => void;
  downloading: boolean;
  focusId?: string | undefined;
  focusRequest?: number;
}

function ShareDocument({
  document,
  snapshot,
  busy,
  onAction,
}: FieldPanelProps & { document: FieldDocument }) {
  const [actorId, setActorId] = useState<FieldActorId>('noor');
  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void onAction({ type: 'document.share', documentId: document.id, actorIds: [actorId] });
      }}
    >
      <div>
        <label htmlFor={`share-${document.id}`} className={FIELD_LABEL}>
          Share this version with
        </label>
        <SelectField
          id={`share-${document.id}`}
          value={actorId}
          onValueChange={(nextValue) => {
            const actor = snapshot.actors.find((person) => person.id === nextValue);
            if (actor) setActorId(actor.id);
          }}
          options={[
            ...(snapshot.actors.map((actor) => ({ value: actor.id, label: actor.name })) ?? []),
          ]}
        />
      </div>
      <Button
        type="submit"
        size="sm"
        variant="secondary"
        disabled={busy || document.status !== 'published'}
      >
        Share version {document.version}
      </Button>
    </form>
  );
}

export function FieldDocuments(props: Props) {
  const { snapshot, busy, onAction, downloading, onDownload } = props;
  const [creating, setCreating] = useState<'request' | 'delivery' | null>(null);
  const [search, setSearch] = useState('');
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!props.focusId) return;
    setSearch('');
    const frame = requestAnimationFrame(() => {
      const element = Array.from(
        panel.current?.querySelectorAll<HTMLElement>('[data-object-id]') ?? [],
      ).find((element) => element.dataset.objectId === props.focusId);
      element?.focus({ preventScroll: true });
      element?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [props.focusId, props.focusRequest]);
  const documents = snapshot.documents.filter((document) =>
    `${document.title} ${documentKindLabels[document.kind] ?? document.kind}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const collections = [
    {
      title: 'Your deliveries',
      description: 'Work you recorded',
      documents: documents.filter((document) => document.author === 'learner'),
    },
    {
      title: 'Reference documents',
      description: 'Prepared by colleagues',
      documents: documents.filter((document) => document.author !== 'learner'),
    },
  ];
  const [actorId, setActorId] = useState<FieldActorId>('noor');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<DocumentKind>('architecture');
  const [instructions, setInstructions] = useState('');
  const [source, setSource] = useState('');
  const [requested, setRequested] = useState(false);
  const authorKinds = kinds.filter((item) => canAuthorFieldDocument(actorId, item));
  return (
    <div ref={panel} className="field-object-panel h-full overflow-y-auto">
      <FieldPanelHeading
        tourTarget="documents"
        title="Documents & deliveries"
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => setCreating(creating === 'delivery' ? null : 'delivery')}
              aria-expanded={creating === 'delivery'}
            >
              <Plus className="size-4" aria-hidden="true" />
              New delivery
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!snapshot.capabilities.documents}
              onClick={() => setCreating(creating === 'request' ? null : 'request')}
              aria-expanded={creating === 'request'}
            >
              Request document
            </Button>
          </div>
        }
      >
        Keep your engineering work separate from reference material. Share a specific version when
        it is ready.
      </FieldPanelHeading>
      <form
        hidden={creating !== 'request'}
        className="field-object-form space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!snapshot.capabilities.documents || !canAuthorFieldDocument(actorId, kind)) return;
          setRequested(false);
          void onAction({
            type: 'document.request',
            actorId,
            kind,
            title,
            instructions,
            sourceIds: source ? [source] : [],
          }).then((sent) => {
            if (sent) {
              setCreating(null);
              // The command only queues generation. Keep the draft recoverable if that job fails.
              setRequested(true);
            }
          });
        }}
      >
        <div className="field-object-form-grid">
          <div>
            <label htmlFor="doc-author" className={FIELD_LABEL}>
              Ask
            </label>
            <SelectField
              id="doc-author"
              value={actorId}
              onValueChange={(nextValue) => {
                const actor = snapshot.actors.find((person) => person.id === nextValue);
                if (actor) {
                  setActorId(actor.id);
                  if (!canAuthorFieldDocument(actor.id, kind)) {
                    const nextKind = kinds.find((item) => canAuthorFieldDocument(actor.id, item));
                    if (nextKind) setKind(nextKind);
                  }
                }
              }}
              options={[
                ...(snapshot.actors.map((actor) => ({ value: actor.id, label: actor.name })) ?? []),
              ]}
            />
          </div>
          <div>
            <label htmlFor="doc-kind" className={FIELD_LABEL}>
              Document type
            </label>
            <SelectField
              id="doc-kind"
              value={kind}
              onValueChange={(nextValue) => {
                const next = authorKinds.find((item) => item === nextValue);
                if (next) setKind(next);
              }}
              options={[
                ...(authorKinds.map((item) => ({
                  value: item,
                  label: documentKindLabels[item] ?? item,
                })) ?? []),
              ]}
            />
          </div>
        </div>
        <div>
          <label htmlFor="doc-title" className={FIELD_LABEL}>
            Title
          </label>
          <input
            id="doc-title"
            className={FIELD_INPUT}
            required
            maxLength={200}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="doc-instructions" className={FIELD_LABEL}>
            What do you need clarified or recorded?
          </label>
          <textarea
            id="doc-instructions"
            className={FIELD_INPUT}
            rows={3}
            maxLength={8000}
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
          />
        </div>
        <div>
          <label htmlFor="doc-source" className={FIELD_LABEL}>
            Attach a source
          </label>
          <SelectField
            id="doc-source"
            value={source}
            onValueChange={(nextValue) => setSource(nextValue)}
            options={[
              { value: '', label: 'Use the colleague’s authorized sources' },
              ...(snapshot.prs.map((pr) => ({
                value: pr.id,
                label: (
                  <>
                    Pull request · {pr.title} · {pr.revision.slice(0, 8)}
                  </>
                ),
              })) ?? []),
              ...(snapshot.documents.map((document) => ({
                value: document.id,
                label: (
                  <>
                    {document.title} · version {document.version}
                  </>
                ),
              })) ?? []),
            ]}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            disabled={busy || !title.trim() || !snapshot.capabilities.documents}
          >
            Request document from{' '}
            {snapshot.actors.find((actor) => actor.id === actorId)?.name.split(' ')[0]}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setCreating(null)}>
            Close
          </Button>
        </div>
      </form>
      {requested ? (
        <p role="status" className="text-fg-muted px-5 pt-4 text-sm">
          Document requested. Follow its progress in Runs &amp; release. Your request draft is kept
          here so you can reopen it if generation fails.
        </p>
      ) : null}
      <FieldDeliveryForm
        {...props}
        open={creating === 'delivery'}
        onClose={() => setCreating(null)}
      />
      <div className="field-object-toolbar">
        <div className="field-object-search">
          <label htmlFor="document-search" className={FIELD_LABEL}>
            Find a document
          </label>
          <input
            id="document-search"
            type="search"
            className={FIELD_INPUT}
            placeholder="Search title or document type"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>
      {downloading ? (
        <LoadingStatus label="Preparing document download…" className="px-5 pt-5" />
      ) : null}
      {!snapshot.documents.length ? (
        <FieldEmpty title="Your documents will appear here">
          Record your first delivery, or ask a colleague for a reference document.
        </FieldEmpty>
      ) : !documents.length ? (
        <FieldEmpty title="No documents match your search">
          Try another title or document type.
        </FieldEmpty>
      ) : (
        <div className="field-object-collection">
          {collections.map((collection) => (
            <section key={collection.title} aria-label={collection.title}>
              <div className="field-object-collection-heading">
                <div>
                  <h3>{collection.title}</h3>
                  <p className="text-fg-muted mt-1 text-sm">{collection.description}</p>
                </div>
                <span>{collection.documents.length}</span>
              </div>
              {!collection.documents.length ? (
                <p className="text-fg-muted py-4 text-sm">
                  No {collection.title.toLowerCase()} yet.
                </p>
              ) : (
                collection.documents.map((document) => (
                  <article
                    key={document.id}
                    tabIndex={-1}
                    data-object-id={document.id}
                    data-focused={props.focusId === document.id}
                    className="field-object-card"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="text-base font-semibold">{document.title}</h4>
                        <div className="field-object-meta mt-2">
                          <span>Version {document.version}</span>
                          <span>
                            {documentKindLabels[document.kind] ??
                              document.kind.replaceAll('_', ' ')}
                          </span>
                          <span>
                            {document.author === 'learner'
                              ? 'By you'
                              : snapshot.actors.find((actor) => actor.id === document.author)?.name}
                          </span>
                          <FieldTime value={document.createdAt} />
                        </div>
                      </div>
                      <FieldStatus value={document.status} />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {document.formats.map((format) => (
                        <Button
                          key={format}
                          variant="secondary"
                          size="sm"
                          disabled={downloading}
                          aria-label={`Download ${document.title} as ${documentFormatLabels[format]}`}
                          onClick={() => onDownload(document, format)}
                        >
                          <Download className="size-3.5" aria-hidden="true" />
                          Download {documentFormatLabels[format]}
                        </Button>
                      ))}
                      {!document.formats.length ? (
                        <p className="text-fg-muted text-sm">No download is ready yet.</p>
                      ) : null}
                    </div>
                    {document.deliveries.length ? (
                      <p className="text-fg-muted mt-4 text-sm">
                        Delivered to{' '}
                        {document.deliveries
                          .map(
                            (delivery) =>
                              snapshot.actors.find((actor) => actor.id === delivery.actorId)
                                ?.name ?? delivery.actorId,
                          )
                          .join(', ')}
                      </p>
                    ) : (
                      <p className="text-fg-muted mt-4 text-sm">Not shared with a colleague.</p>
                    )}
                    <ShareDocument {...props} document={document} />
                    {document.sourceIds.length ? (
                      <details className="text-fg-muted mt-3 text-sm">
                        <summary className="cursor-pointer">
                          Sources ({document.sourceIds.length})
                        </summary>
                        <ul className="space-y-2 pb-2 pl-4">
                          {document.sourceIds.map((id) => (
                            <li key={id} title={id}>
                              {describeFieldSource(snapshot, id)}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </article>
                ))
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
